import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminInvoicesService } from '../services/admin.invoices.service'
import type {
  ProviderInvoiceDocument,
  ProviderInvoiceLineItem,
  ProviderInvoiceStatus,
} from '@/types'

export type AdminInvoiceRow = ProviderInvoiceDocument & { id: string }

export type StatusCounts = Record<ProviderInvoiceStatus | 'all', number>

const EMPTY_COUNTS: StatusCounts = {
  draft: 0,
  submitted: 0,
  approved: 0,
  rejected: 0,
  paid: 0,
  all: 0,
}

export function useAdminInvoices() {
  const [all, setAll] = useState<AdminInvoiceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<ProviderInvoiceStatus | 'all'>('submitted')
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    adminInvoicesService
      .listAll()
      .then((rows) => {
        if (!cancelled) setAll(rows)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(formatUserFacingError(err, 'Failed to load invoices.'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  const counts: StatusCounts = useMemo(() => {
    const c: StatusCounts = { ...EMPTY_COUNTS, all: all.length }
    for (const inv of all) {
      c[inv.status] = (c[inv.status] ?? 0) + 1
    }
    return c
  }, [all])

  const invoices = useMemo(() => {
    if (filterStatus === 'all') return all
    return all.filter((inv) => inv.status === filterStatus)
  }, [all, filterStatus])

  const approve = useCallback(
    async (id: string) => {
      await adminInvoicesService.approve(id)
      refresh()
    },
    [refresh],
  )

  const reject = useCallback(
    async (id: string, note: string) => {
      await adminInvoicesService.reject(id, note)
      refresh()
    },
    [refresh],
  )

  const markPaid = useCallback(
    async (id: string) => {
      await adminInvoicesService.markPaid(id)
      refresh()
    },
    [refresh],
  )

  const editLineItems = useCallback(
    async (id: string, lineItems: ProviderInvoiceLineItem[]) => {
      await adminInvoicesService.editLineItems(id, lineItems)
      refresh()
    },
    [refresh],
  )

  return {
    invoices,
    counts,
    loading,
    error,
    filterStatus,
    setFilterStatus,
    refresh,
    approve,
    reject,
    markPaid,
    editLineItems,
  }
}
